import { http } from "./http";

/**
 * Customer-facing "Data Sheets" API client. The feature is the new
 * portal's replacement for the legacy <i>Settings → Data</i> / <i>SimpleDb</i>
 * pages. A data sheet has a fixed 10-slot schema — each slot is either
 * empty or "a named, typed column" — and rows are flat records with
 * one string value per slot.
 *
 * All endpoints are tenant-scoped server-side; the SPA just passes the
 * sheet id and trusts 403s to bubble up when the caller doesn't have
 * the right to mutate schema (which requires impersonation).
 */

export interface DataSheetSummary {
  sheetId: number;
  name: string;
  description: string;
  columnCount: number;
}

export interface DataSheetListResponse {
  items: DataSheetSummary[];
}

export interface DataSheetColumn {
  /** 1-based slot index (1..10). Empty slots still have a slot but a
   *  null/empty name. */
  slot: number;
  name: string;
  /** Type id from the shared column-types catalog (see `listColumnTypes`). */
  typeId: number;
}

export interface DataSheetDetail {
  sheetId: number;
  name: string;
  description: string;
  /** Always exactly 10 entries — use the `name` field to tell filled
   *  slots from empty ones. */
  columns: DataSheetColumn[];
}

export interface ColumnType {
  id: number;
  name: string;
}

export interface ColumnTypesResponse {
  items: ColumnType[];
}

export interface LookupItem {
  id: number;
  name: string;
}

/** Tenant-scoped reference lists consumed by reference-typed columns. */
export interface LookupsResponse {
  huntGroups: LookupItem[];
  agents: LookupItem[];
  voices: LookupItem[];
  weekplans: LookupItem[];
}

export interface DataSheetRecord {
  dataId: number;
  /** Exactly 10 entries, indexed by slot-1. Raw DB values; reference
   *  types are stringified ids, booleans are "0"/"1". */
  values: (string | null)[];
  /** Exactly 10 entries; identical to `values` for scalar types but
   *  resolves names for reference types (e.g. "Support agents"). */
  displays: (string | null)[];
}

export interface RecordListResponse {
  items: DataSheetRecord[];
}

export interface UpsertSheetPayload {
  name: string;
  description: string;
  columns: { slot: number; name: string; typeId: number }[];
}

export interface UpsertRecordPayload {
  /** Exactly 10 entries; shorter arrays are padded on the server. */
  values: (string | null)[];
}

const jsonBody = (payload: unknown): RequestInit => ({
  body: JSON.stringify(payload),
});

export const dataSheetsApi = {
  list: () => http<DataSheetListResponse>("/api/data-sheets"),
  get: (sheetId: number) => http<DataSheetDetail>(`/api/data-sheets/${sheetId}`),
  create: (payload: UpsertSheetPayload) =>
    http<DataSheetDetail>("/api/data-sheets", {
      method: "POST",
      ...jsonBody(payload),
    }),
  update: (sheetId: number, payload: UpsertSheetPayload) =>
    http<DataSheetDetail>(`/api/data-sheets/${sheetId}`, {
      method: "PUT",
      ...jsonBody(payload),
    }),
  remove: (sheetId: number) =>
    http<{ ok: boolean }>(`/api/data-sheets/${sheetId}`, { method: "DELETE" }),

  listColumnTypes: () => http<ColumnTypesResponse>("/api/data-sheets/column-types"),
  lookups: () => http<LookupsResponse>("/api/data-sheets/lookups"),

  listRecords: (sheetId: number) =>
    http<RecordListResponse>(`/api/data-sheets/${sheetId}/records`),
  createRecord: (sheetId: number, payload: UpsertRecordPayload) =>
    http<{ dataId: number }>(`/api/data-sheets/${sheetId}/records`, {
      method: "POST",
      ...jsonBody(payload),
    }),
  updateRecord: (sheetId: number, dataId: number, payload: UpsertRecordPayload) =>
    http<{ dataId: number }>(`/api/data-sheets/${sheetId}/records/${dataId}`, {
      method: "PUT",
      ...jsonBody(payload),
    }),
  removeRecord: (sheetId: number, dataId: number) =>
    http<{ ok: boolean }>(`/api/data-sheets/${sheetId}/records/${dataId}`, {
      method: "DELETE",
    }),
};

/**
 * Stable integer identifiers for the built-in column types as they
 * appear in the <c>SimpleDbColType</c> catalog. Kept in sync with the
 * legacy portal's <c>RecordDetails.cshtml</c> switch. Keep in mind the
 * SPA only needs to branch on these for the inline editor renderer;
 * the API is agnostic and just stores whatever string we send it.
 */
export const DATA_SHEET_COL_TYPE = {
  text: 1,
  number: 2,
  checkbox: 3,
  huntGroup: 4,
  agent: 5,
  voice: 6,
  weekplan: 7,
  date: 8,
  time: 9,
} as const;

export type DataSheetColTypeId = (typeof DATA_SHEET_COL_TYPE)[keyof typeof DATA_SHEET_COL_TYPE];
